import type { ValidationRule } from "./types";

export const REQUIRED_VALIDATION_MESSAGE = "This field is required";

/**
 * Builds the `validate` function for `Field.Root`.
 *
 * Returns the first failing rule's message, or `null` when valid.
 * The `required` whitespace check is baked in — the browser's native
 * `valueMissing` only catches empty strings, not spaces-only input.
 *
 * The returned function is synchronous when every rule is synchronous.
 * Base UI's Form reads validation state synchronously on submit
 * ("Async validation isn't supported to stop the submit event"), so an
 * async validate function causes the first submit to be silently blocked
 * while validation is still in flight.
 */
export function composeValidate(
  validations?: ValidationRule[],
  required?: boolean,
  requiredMessage = REQUIRED_VALIDATION_MESSAGE,
) {
  if (!validations?.length && !required) return undefined;

  return (value: unknown): string | null | Promise<string | null> => {
    const input = String(value ?? "");

    if (required && input.length > 0 && input.trim() === "") {
      return requiredMessage;
    }

    if (validations) {
      for (const rule of validations) {
        const result = rule.match(input);

        // If any rule returns a Promise, await the remainder asynchronously.
        if (typeof result === "object" && result !== null && "then" in result) {
          return (async () => {
            if (await result) return rule.message;

            // Continue checking the remaining rules
            const remaining = validations.slice(validations.indexOf(rule) + 1);
            for (const r of remaining) {
              if (await r.match(input)) return r.message;
            }
            return null;
          })();
        }

        if (result) return rule.message;
      }
    }

    return null;
  };
}
